import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, BookOpen, Lightbulb } from 'lucide-react';

interface ChartExplainerProps {
  title: string;
  explanation: string;
  howToRead?: string[];
  tips?: string[];
  examples?: string[];
}

export const ChartExplainer: React.FC<ChartExplainerProps> = ({
  title,
  explanation,
  howToRead,
  tips,
  examples
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg transition-all group"
      >
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
            <HelpCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="text-left">
            <h5 className="text-sm font-semibold text-white">Come leggere: {title}</h5>
            <p className="text-xs text-gray-400 mt-0.5">
              {isExpanded ? 'Nascondi' : 'Mostra'} guida dettagliata
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-blue-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-blue-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 p-5 bg-gray-800/50 border border-gray-700 rounded-lg space-y-4 animate-in fade-in duration-300">
          <div>
            <p className="text-sm text-gray-300 leading-relaxed">{explanation}</p>
          </div>

          {howToRead && howToRead.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <div className="p-1.5 bg-green-500/20 rounded">
                  <BookOpen className="h-4 w-4 text-green-400" />
                </div>
                <h6 className="text-sm font-semibold text-white">Come Interpretare</h6>
              </div>
              <ul className="space-y-2 ml-7">
                {howToRead.map((item, index) => (
                  <li key={index} className="text-sm text-gray-300 leading-relaxed flex items-start">
                    <span className="text-green-400 mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tips && tips.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <div className="p-1.5 bg-yellow-500/20 rounded">
                  <Lightbulb className="h-4 w-4 text-yellow-400" />
                </div>
                <h6 className="text-sm font-semibold text-white">Consigli Pratici</h6>
              </div>
              <ul className="space-y-2 ml-7">
                {tips.map((tip, index) => (
                  <li key={index} className="text-sm text-gray-300 leading-relaxed flex items-start">
                    <span className="text-yellow-400 mr-2">💡</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {examples && examples.length > 0 && (
            <div className="pt-3 border-t border-gray-700">
              <h6 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Esempi Pratici
              </h6>
              <div className="space-y-2">
                {examples.map((example, index) => (
                  <div key={index} className="p-3 bg-gray-900/50 rounded border border-gray-700">
                    <p className="text-sm text-gray-300 leading-relaxed">{example}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
